import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import {
  clearPendingGoogleAccount,
  getPendingGoogleAccount,
  markNewSignupFlow,
  splitDisplayNameToThree,
} from '../../utils/googleAuth';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import SignupStepBadge from '../../components/auth/SignupStepBadge';

const GoogleCreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { isRTL, language } = useLocalization();
  const pending = useMemo(() => getPendingGoogleAccount(), []);
  const names = useMemo(
    () => splitDisplayNameToThree(pending?.displayName || ''),
    [pending]
  );
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pending) {
      navigate('/login', { replace: true });
    }
  }, [pending, navigate]);

  const onCancel = () => {
    clearPendingGoogleAccount();
    navigate('/login', { replace: true });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await AuthService.completeGoogleRegistration({
        firstName: names.firstName,
        middleName: names.middleName,
        lastName: names.lastName,
        acceptTerms,
        acceptPrivacy,
      });
      setUser(user);
      markNewSignupFlow(true);
      navigate('/auth/demographics', { replace: true });
    } catch (err: any) {
      setError(err?.message || (isRTL ? 'فشل إنشاء الحساب' : 'Failed to create account'));
    } finally {
      setLoading(false);
    }
  };

  if (!pending) return null;

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 py-4 px-4">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MorafaaLogo size="sm" variant="light" />
            <span className="text-white font-bold">{isRTL ? 'مرافعة' : 'Morafaa'}</span>
          </div>
          <button type="button" onClick={onCancel} className="text-white/80 text-sm">
            {isRTL ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>

      <div className="flex justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 space-y-4">
          <SignupStepBadge step={1} isRTL={isRTL} />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isRTL ? 'إنشاء حساب' : 'Create Account'}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {isRTL
              ? 'أكد بيانات حساب Google واقبل الشروط لإنشاء حسابك.'
              : 'Confirm your Google account details and accept the terms to create your account.'}
          </p>
          {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

          {(['firstName', 'middleName', 'lastName', 'email'] as const).map((key) => {
            const label =
              key === 'firstName'
                ? isRTL ? 'الاسم الأول' : 'First name'
                : key === 'middleName'
                  ? isRTL ? 'الاسم الأوسط' : 'Middle name'
                  : key === 'lastName'
                    ? isRTL ? 'اسم العائلة' : 'Last name'
                    : isRTL ? 'البريد الإلكتروني' : 'Email';
            const value =
              key === 'email'
                ? pending.email
                : key === 'firstName'
                  ? names.firstName
                  : key === 'middleName'
                    ? names.middleName
                    : names.lastName;
            return (
              <div key={key}>
                <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">{label}</label>
                <input
                  value={value}
                  disabled
                  className="w-full px-3 py-3 rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                />
              </div>
            );
          })}

          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
            <span>
              {isRTL ? 'أوافق على' : 'I agree to the'}{' '}
              <Link to="/terms-and-conditions" className="text-primary underline">
                {isRTL ? 'الشروط والأحكام' : 'Terms'}
              </Link>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} />
            <span>
              {isRTL ? 'أوافق على' : 'I agree to the'}{' '}
              <Link to="/privacy-policy" className="text-primary underline">
                {isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !acceptTerms || !acceptPrivacy}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium disabled:opacity-50"
          >
            {loading
              ? language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'
              : language === 'ar' ? 'إنشاء حساب' : 'Create Account'}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default GoogleCreateAccount;
